import { createMemo, createSignal, Show, type Component } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { createQuery } from '@tanstack/solid-query';
import { queryView } from '@sportpark/shared/query-view';
import { useCan } from '~/shared/access/useCan';
import { cn } from '~/shared/lib/cn';
import { ChevronDown, HelpCircle, KeyRound, LogOut, MessageSquare, Search, Settings, ShieldOff, UserRound, Users } from 'lucide-solid';
import { useToast } from '~/shared/ui/Toast';
import { useAuth } from '~/shared/api/auth-context';
import { chatApi } from '~/shared/api/chat-api';
import { queryKeys } from '~/shared/api/query-keys';
import { useChatPanel } from '~/features/chat/ChatPanelProvider';
import { authApi } from '~/shared/api/auth-api';
import { useCommandPalette } from '~/widgets/shell/CommandPalette';
import { ShortcutsDialog } from '~/shared/ui/ShortcutsDialog';
import { ChangePasswordDialog } from '~/widgets/shell/ChangePasswordDialog';
import { MyProfileDialog } from '~/widgets/shell/MyProfileDialog';
import { ThemeToggle } from '~/shared/ui/ThemeToggle';
import { ClubJoinCodeBadge } from './ClubJoinCodeBadge';
import { menuEntry } from '~/shared/access/sections';
import { NotificationsMenu } from '~/widgets/shell/NotificationsMenu';
import { Popover } from '~/shared/ui/Popover';
import { useI18n } from '~/shared/lib/i18n';
import { useShortcut } from '~/shared/lib/shortcuts';
import { initials } from '~/shared/lib/formatters';
import { ConnectionStatus } from '~/shared/realtime';

export const TopBar: Component = () => {
  const { t } = useI18n();
  const { session, signOut, renameUser } = useAuth();
  const palette = useCommandPalette();
  const navigate = useNavigate();
  const toast = useToast();

  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);
  const [passwordOpen, setPasswordOpen] = createSignal(false);
  const [myProfileOpen, setMyProfileOpen] = createSignal(false);
  const [profileOpen, setProfileOpen] = createSignal(false);
  const [profileAnchor, setProfileAnchor] = createSignal<HTMLButtonElement>();

  useShortcut('cmd+/', () => setShortcutsOpen(true), { preventDefault: true, allowInInputs: true });
  useShortcut('ctrl+/', () => setShortcutsOpen(true), { preventDefault: true, allowInInputs: true });

  const doSignOut = async () => {
    await authApi.logout();
    signOut();
    navigate('/login');
  };

  const doSignOutEverywhere = async () => {
    if (!window.confirm('Завершить сессии на всех устройствах? Вы выйдете и здесь.')) return;
    try {
      await authApi.logoutEverywhere();
    } catch {
      toast.error('Не удалось завершить сессии. Сессии остались активны — смените пароль.');
      return;
    }
    signOut();
    navigate('/login');
  };

  const userEmail = () => session()?.user.email ?? '';
  const userInitials = () => initials(userEmail().split('@')[0] ?? '');

  return (
    <>
      <header class="sticky top-4 z-20 mx-4 glass h-14 flex items-center px-3 gap-3 min-w-0">
        <button
          type="button"
          class="press flex items-center justify-center lg:justify-start gap-3 h-9 w-9 shrink-0 lg:w-auto lg:shrink lg:flex-1 min-w-0 lg:max-w-[520px] lg:px-3 rounded-md bg-white/5 text-left border border-hairline hover:bg-white/8 transition-colors"
          aria-label={t('topbar.commandPlaceholder')}
          onClick={() => palette.show()}
        >
          <Search class="h-4 w-4 shrink-0 text-tertiary" />
          <span class="hidden lg:block text-[13px] text-tertiary flex-1 min-w-0 truncate">
            {t('topbar.commandPlaceholder')}
          </span>
          <kbd class="hidden lg:inline shrink-0 tabular text-[11px] text-tertiary px-1.5 py-0.5 rounded bg-white/8 border border-hairline">
            {t('topbar.commandShortcut')}
          </kbd>
        </button>

        <div class="flex-1" />

        <button
          type="button"
          class="press h-9 w-9 flex items-center justify-center rounded-md hover:bg-white/6 text-secondary"
          aria-label={t('shortcuts.dialogTitle')}
          title={t('shortcuts.dialogTitle')}
          onClick={() => setShortcutsOpen(true)}
        >
          <HelpCircle class="h-[18px] w-[18px]" />
        </button>

        <ThemeToggle />

        <ConnectionStatus />

        <ChatButton />

        <NotificationsMenu />

        <button
          type="button"
          class="press flex items-center gap-2 h-9 px-3 rounded-md hover:bg-white/6 text-[13px]"
          onClick={() => navigate('/login/tenant')}
        >
          <span class="w-5 h-5 rounded-full bg-gradient-to-br from-accent to-purple" />
          <span class="text-primary truncate max-w-[96px] xl:max-w-[140px]">
            {session()?.user.tenantName ?? 'Без клуба'}
          </span>
          <ChevronDown class="h-3.5 w-3.5 text-tertiary" />
        </button>

        <ClubJoinCodeBadge />

        <div class="w-px h-6 bg-hairline mx-1" />

        <div class="relative">
          <button
            type="button"
            ref={setProfileAnchor}
            class="press flex items-center gap-2 h-9 pl-1 pr-2 rounded-md hover:bg-white/6 text-[13px]"
            onClick={() => setProfileOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={profileOpen()}
          >
            <span class="w-7 h-7 rounded-full bg-gradient-to-br from-teal to-accent flex items-center justify-center text-[11px] font-semibold">
              {userInitials()}
            </span>
            <ChevronDown class="h-3.5 w-3.5 text-tertiary" />
          </button>
          <Popover
            open={profileOpen()}
            onClose={() => setProfileOpen(false)}
            anchor={profileAnchor()}
            align="end"
          >
            <div role="menu" class="glass-menu w-60 p-1 animate-menu-in origin-top-right">
              <div class="px-3 py-2 border-b border-hairline mb-1">
                <p class="text-[13px] font-medium truncate">{userEmail()}</p>
                <p class="text-[11px] text-tertiary">
                  {session()?.user.roles.join(' · ') || '—'}
                </p>
              </div>
              <MenuItem
                icon={<Users class="h-4 w-4" />}
                label={t('topbar.switchTenant')}
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/login/tenant');
                }}
              />
              <Show when={menuEntry('/settings', session()?.user.permissions ?? [])}>
                {(href) => (
                  <MenuItem
                    icon={<Settings class="h-4 w-4" />}
                    label={t('nav.settings')}
                    onClick={() => {
                      setProfileOpen(false);
                      navigate(href());
                    }}
                  />
                )}
              </Show>
              <MenuItem
                icon={<UserRound class="h-4 w-4" />}
                label="Мой профиль"
                onClick={() => {
                  setProfileOpen(false);
                  setMyProfileOpen(true);
                }}
              />
              <MenuItem
                icon={<HelpCircle class="h-4 w-4" />}
                label={t('topbar.shortcuts')}
                onClick={() => {
                  setProfileOpen(false);
                  setShortcutsOpen(true);
                }}
              />
              <MenuItem
                icon={<KeyRound class="h-4 w-4" />}
                label="Сменить пароль"
                onClick={() => {
                  setProfileOpen(false);
                  setPasswordOpen(true);
                }}
              />
              <div class="my-1 border-t border-hairline" />
              <MenuItem
                icon={<LogOut class="h-4 w-4" />}
                label={t('topbar.signOut')}
                destructive
                onClick={() => {
                  setProfileOpen(false);
                  void doSignOut();
                }}
              />
              <MenuItem
                icon={<ShieldOff class="h-4 w-4" />}
                label="Выйти со всех устройств"
                destructive
                onClick={() => {
                  setProfileOpen(false);
                  void doSignOutEverywhere();
                }}
              />

              <style>{`
                @keyframes menu-in {
                  from { transform: scale(0.96) translateY(-4px); opacity: 0 }
                  to   { transform: scale(1) translateY(0);       opacity: 1 }
                }
                .animate-menu-in { animation: menu-in 160ms var(--ease-ios) both; }
              `}</style>
            </div>
          </Popover>
        </div>
      </header>

      <ShortcutsDialog open={shortcutsOpen()} onClose={() => setShortcutsOpen(false)} />
      <Show when={myProfileOpen()}>
        <MyProfileDialog
          open
          user={session()?.user}
          onClose={() => setMyProfileOpen(false)}
          onRenamed={(name) => {
            renameUser(name);
            setMyProfileOpen(false);
            toast.success('Имя сохранено');
          }}
          onSignedOut={() => {
            setMyProfileOpen(false);
            toast.success('Номер сменён. Войдите заново — телефон стал вашим новым логином.');
            signOut();
            navigate('/login');
          }}
        />
      </Show>
      <ChangePasswordDialog
        open={passwordOpen()}
        onClose={() => setPasswordOpen(false)}
        onChanged={() => {
          setPasswordOpen(false);
          signOut();
          navigate('/login');
        }}
      />
    </>
  );
};

export const ChatButton: Component = () => {
  const chat = useChatPanel();
  const canRespond = useCan('/clients', 'chat');

  const inbox = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: queryKeys.chat.conversations(),
    queryFn: ({ signal }) => chatApi.listConversations({ signal }),
    enabled: canRespond(),
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: false,
  }));

  const unread = createMemo(() =>
    (inbox.data ?? []).reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),
  );
  const inboxView = queryView(inbox);
  const unreadUnknown = () => inboxView.isError && !inboxView.hasData;

  return (
    <Show when={canRespond()}>
      <button
        type="button"
        class="press relative h-9 w-9 flex items-center justify-center rounded-md hover:bg-white/6 text-secondary"
        aria-label="Чат поддержки"
        title="Чат поддержки"
        onClick={() => chat.open()}
      >
        <MessageSquare class="h-[18px] w-[18px]" />
        <Show when={unread() > 0 || unreadUnknown()}>
          <span class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none flex items-center justify-center tabular shadow-[0_0_0_2px_var(--color-bg-glass)]">
            {unreadUnknown() ? '?' : unread() > 9 ? '9+' : unread()}
          </span>
        </Show>
      </button>
    </Show>
  );
};

import type { JSX } from 'solid-js';

interface MenuItemProps {
  icon?: JSX.Element;
  label: string;
  destructive?: boolean;
  onClick: () => void;
}

const MenuItem: Component<MenuItemProps> = (props) => (
  <button
    type="button"
    role="menuitem"
    class={cn(
      'press w-full flex items-center gap-2.5 px-3 h-9 rounded-sm text-[13px] text-left',
      props.destructive
        ? 'text-danger hover:bg-[hsl(4_90%_63%/0.14)]'
        : 'text-primary hover:bg-white/6',
    )}
    onClick={props.onClick}
  >
    <Show when={props.icon}>
      <span class="text-tertiary">{props.icon}</span>
    </Show>
    <span class="flex-1">{props.label}</span>
  </button>
);
